const DEFAULT_SLOPE_TOLERANCE = 0.006;
const MIN_SLOPE_TO_CHECK = 0.003;
const ADJACENT_POLYGON_Y_GAP = 0.025;
const RIGHT_EDGE_MIN_X = 0.72;

function median(values, fallback = 0) {
  const sorted = values
    .filter((value) => Number.isFinite(value))
    .sort((left, right) => left - right);
  if (!sorted.length) return fallback;
  return sorted[Math.floor(sorted.length / 2)];
}

function slopeBetween(left, right) {
  const dx = (right?.x ?? 0) - (left?.x ?? 0);
  if (Math.abs(dx) < 0.003) return null;
  const slope = ((right?.y ?? 0) - (left?.y ?? 0)) / dx;
  return Math.abs(slope) <= 0.25 ? slope : null;
}

function boundsOfVertices(vertices) {
  if (!Array.isArray(vertices) || vertices.length < 3) return null;
  const xs = vertices.map((vertex) => vertex.x).filter(Number.isFinite);
  const ys = vertices.map((vertex) => vertex.y).filter(Number.isFinite);
  if (!xs.length || !ys.length) return null;
  return {
    minX: Math.min(...xs),
    maxX: Math.max(...xs),
    minY: Math.min(...ys),
    maxY: Math.max(...ys),
    centerY: (Math.min(...ys) + Math.max(...ys)) / 2,
  };
}

function tokenCenterX(token) {
  return token.centerX ?? (token.minX + token.maxX) / 2;
}

function polygonYRangeAtX(vertices, sampleX) {
  if (
    !Array.isArray(vertices) ||
    vertices.length < 3 ||
    !Number.isFinite(sampleX)
  ) {
    return null;
  }

  const intersections = [];
  for (let index = 0; index < vertices.length; index += 1) {
    const start = vertices[index];
    const end = vertices[(index + 1) % vertices.length];
    const minX = Math.min(start.x, end.x);
    const maxX = Math.max(start.x, end.x);
    if (sampleX < minX - 1e-6 || sampleX > maxX + 1e-6) continue;

    const dx = end.x - start.x;
    if (Math.abs(dx) < 1e-6) {
      intersections.push(start.y, end.y);
      continue;
    }

    const t = (sampleX - start.x) / dx;
    if (t < -1e-6 || t > 1 + 1e-6) continue;
    intersections.push(start.y + (end.y - start.y) * t);
  }

  const uniqueYs = [
    ...new Set(intersections.map((value) => Number(value.toFixed(6)))),
  ];
  if (uniqueYs.length < 2) return null;
  return {
    top: Math.min(...uniqueYs),
    bottom: Math.max(...uniqueYs),
  };
}

function lineYAtX(left, right, sampleX) {
  if (!left || !right || !Number.isFinite(sampleX)) return null;
  const dx = right.x - left.x;
  if (Math.abs(dx) < 1e-6) return null;
  const t = (sampleX - left.x) / dx;
  if (t < -1e-6 || t > 1 + 1e-6) return null;
  return left.y + (right.y - left.y) * t;
}

function tokenYRangeAtX(token, sampleX) {
  const ordered = token?.ordered;
  if (!Array.isArray(ordered) || ordered.length < 4) {
    if (!Number.isFinite(token?.minY) || !Number.isFinite(token?.maxY))
      return null;
    return { top: token.minY, bottom: token.maxY };
  }

  const top = lineYAtX(ordered[0], ordered[1], sampleX);
  const bottom = lineYAtX(ordered[3], ordered[2], sampleX);
  if (!Number.isFinite(top) || !Number.isFinite(bottom)) {
    return {
      top: token.minY,
      bottom: token.maxY,
    };
  }

  return {
    top: Math.min(top, bottom),
    bottom: Math.max(top, bottom),
  };
}

function getRowPolygons(row) {
  if (Array.isArray(row?.normalizedPolygons) && row.normalizedPolygons.length) {
    return row.normalizedPolygons.filter((polygon) => polygon?.length >= 3);
  }
  if (row?.normalizedVertices?.length >= 3) return [row.normalizedVertices];
  return [];
}

function getQuadCorners(vertices) {
  if (!Array.isArray(vertices) || vertices.length < 4) return null;

  const centroid = vertices.reduce(
    (accumulator, vertex) => ({
      x: accumulator.x + vertex.x,
      y: accumulator.y + vertex.y,
    }),
    { x: 0, y: 0 },
  );
  centroid.x /= vertices.length;
  centroid.y /= vertices.length;

  const ordered = [...vertices].sort((left, right) => {
    const leftAngle = Math.atan2(left.y - centroid.y, left.x - centroid.x);
    const rightAngle = Math.atan2(right.y - centroid.y, right.x - centroid.x);
    return leftAngle - rightAngle;
  });

  let topLeftIndex = 0;
  let topLeftScore = Infinity;
  ordered.forEach((vertex, index) => {
    const score = vertex.x + vertex.y;
    if (score < topLeftScore) {
      topLeftScore = score;
      topLeftIndex = index;
    }
  });

  let corners = [
    ...ordered.slice(topLeftIndex),
    ...ordered.slice(0, topLeftIndex),
  ];

  if (corners[1].x < corners[corners.length - 1].x) {
    corners = [corners[0], ...corners.slice(1).reverse()];
  }

  return {
    tl: corners[0],
    tr: corners[1],
    br: corners[2],
    bl: corners[3],
  };
}

function polygonSlope(vertices) {
  const corners = getQuadCorners(vertices);
  if (!corners) return null;
  const topSlope = slopeBetween(corners.tl, corners.tr);
  const bottomSlope = slopeBetween(corners.bl, corners.br);
  const values = [topSlope, bottomSlope].filter(Number.isFinite);
  if (!values.length) return null;
  return median(values);
}

function tokenTextSlope(token) {
  const ordered = token?.ordered;
  if (!Array.isArray(ordered) || ordered.length < 4) return null;
  const topSlope = slopeBetween(ordered[0], ordered[1]);
  const bottomSlope = slopeBetween(ordered[3], ordered[2]);
  const values = [topSlope, bottomSlope].filter(Number.isFinite);
  if (!values.length) return null;
  return median(values);
}

// True row baseline: least-squares slope of token CENTERS across the row width.
// This is independent of the rendered band's slope source, so comparing it to the
// polygon slope (slope_mismatch) actually catches a band that fails to follow the
// text tilt — unlike the per-token internal slant, which the band itself used to
// be derived from (making the old check tautological).
function baselineSlopeThroughCenters(tokens) {
  // Measure the clean printed-line direction from the code/name/company columns
  // only. The far-right money column curls up off the text baseline on this
  // photographed page, so including it would report the flat code→money chord as
  // the "true" slope and wrongly flag a band that correctly follows the text.
  const left = (tokens ?? []).filter(
    (token) => tokenCenterX(token) <= RIGHT_EDGE_MIN_X,
  );
  const source = left.length >= 4 ? left : (tokens ?? []);
  const cx = [];
  const cy = [];
  source.forEach((token) => {
    const x = tokenCenterX(token);
    const y = token.centerY ?? (token.minY + token.maxY) / 2;
    if (Number.isFinite(x) && Number.isFinite(y)) {
      cx.push(x);
      cy.push(y);
    }
  });
  if (cx.length < 4) return null;
  const mx = cx.reduce((sum, value) => sum + value, 0) / cx.length;
  const my = cy.reduce((sum, value) => sum + value, 0) / cy.length;
  let num = 0;
  let den = 0;
  for (let i = 0; i < cx.length; i += 1) {
    num += (cx[i] - mx) * (cy[i] - my);
    den += (cx[i] - mx) ** 2;
  }
  return den > 1e-9 ? num / den : null;
}

function getTokenStats(sourceRows) {
  const tokens = (sourceRows ?? []).flatMap((row) => row?.tokenVertices ?? []);
  if (!tokens.length) return null;

  const bounds = {
    minX: Math.min(
      ...tokens.map((token) => token.minX).filter(Number.isFinite),
    ),
    maxX: Math.max(
      ...tokens.map((token) => token.maxX).filter(Number.isFinite),
    ),
    minY: Math.min(
      ...tokens.map((token) => token.minY).filter(Number.isFinite),
    ),
    maxY: Math.max(
      ...tokens.map((token) => token.maxY).filter(Number.isFinite),
    ),
  };
  const heights = tokens.map((token) => token.maxY - token.minY);
  const perTokenSlopes = tokens.map(tokenTextSlope).filter(Number.isFinite);
  // Prefer the across-row baseline; fall back to per-token slant for sparse rows.
  const baseline = baselineSlopeThroughCenters(tokens);
  const slope = Number.isFinite(baseline)
    ? baseline
    : median(perTokenSlopes, 0);

  if (!Number.isFinite(bounds.minX) || !Number.isFinite(bounds.maxX))
    return null;

  return {
    tokens,
    tokenCount: tokens.length,
    bounds,
    medianHeight: median(heights, 0.01),
    slope,
    slopeSampleCount: perTokenSlopes.length,
  };
}

function formatNumber(value) {
  return Number.isFinite(value) ? Number(value.toFixed(5)) : null;
}

function getRightEdgeTokens(tokens) {
  const sortedCenters = tokens
    .map(tokenCenterX)
    .filter(Number.isFinite)
    .sort((left, right) => left - right);
  if (!sortedCenters.length) return [];

  const quantileIndex = Math.max(
    0,
    Math.floor((sortedCenters.length - 1) * 0.7),
  );
  const threshold = Math.max(
    RIGHT_EDGE_MIN_X,
    sortedCenters[quantileIndex] - 0.015,
  );
  return tokens.filter((token) => tokenCenterX(token) >= threshold);
}

function measureLocalCoverage(polygon, tokens) {
  const maxima = {
    topExcess: 0,
    topMiss: 0,
    bottomExcess: 0,
    bottomMiss: 0,
    sampleCount: 0,
  };

  tokens.forEach((token) => {
    [token.minX + 0.0005, tokenCenterX(token), token.maxX - 0.0005]
      .filter(Number.isFinite)
      .forEach((sampleX) => {
        const range = polygonYRangeAtX(polygon, sampleX);
        const tokenRange = tokenYRangeAtX(token, sampleX);
        if (!range || !tokenRange) return;
        maxima.sampleCount += 1;
        maxima.topExcess = Math.max(
          maxima.topExcess,
          tokenRange.top - range.top,
        );
        maxima.topMiss = Math.max(maxima.topMiss, range.top - tokenRange.top);
        maxima.bottomExcess = Math.max(
          maxima.bottomExcess,
          range.bottom - tokenRange.bottom,
        );
        maxima.bottomMiss = Math.max(
          maxima.bottomMiss,
          tokenRange.bottom - range.bottom,
        );
      });
  });

  return maxima;
}

function pushCoverageIssue(issues, code, maxima, allowance, missAllowance) {
  if (!maxima.sampleCount) return;
  const hasExcess =
    maxima.topExcess > allowance || maxima.bottomExcess > allowance;
  const hasMiss =
    maxima.topMiss > missAllowance || maxima.bottomMiss > missAllowance;

  if (hasExcess || hasMiss) {
    issues.push({
      code,
      topExcess: formatNumber(maxima.topExcess),
      topMiss: formatNumber(maxima.topMiss),
      bottomExcess: formatNumber(maxima.bottomExcess),
      bottomMiss: formatNumber(maxima.bottomMiss),
      allowance: formatNumber(allowance),
      missAllowance: formatNumber(missAllowance),
      sampleCount: maxima.sampleCount,
    });
  }
}

function addTokenLocalCoverageIssues(issues, polygon, tokenStats) {
  const allowance = Math.max(0.006, tokenStats.medianHeight * 0.85);
  const missAllowance = Math.max(0.004, tokenStats.medianHeight * 0.55);
  const maxima = measureLocalCoverage(polygon, tokenStats.tokens);
  pushCoverageIssue(
    issues,
    'token_local_coverage',
    maxima,
    allowance,
    missAllowance,
  );
}

function addRowLocalCoverageIssues(issues, polygon, tokenStats) {
  const rightTokens = getRightEdgeTokens(tokenStats.tokens);
  if (!rightTokens.length) return;

  const allowance = Math.max(0.005, tokenStats.medianHeight * 0.65);
  const missAllowance = Math.max(0.0035, tokenStats.medianHeight * 0.45);
  const maxima = measureLocalCoverage(polygon, rightTokens);
  pushCoverageIssue(
    issues,
    'right_edge_local_coverage',
    maxima,
    allowance,
    missAllowance,
  );
}

function buildRowDiagnostics(row, sourceRows, index) {
  const polygons = getRowPolygons(row);
  const polygon = polygons[0];
  const polygonBounds = boundsOfVertices(polygon);
  const tokenStats = getTokenStats(sourceRows);
  const issues = [];

  if (!polygon || !polygonBounds) {
    issues.push({ code: 'missing_polygon' });
    return { rowId: row.rowId, index, ok: false, issues };
  }

  if (!tokenStats) {
    issues.push({ code: 'missing_source_tokens' });
    return { rowId: row.rowId, index, ok: false, issues };
  }

  const highlightSlope = polygonSlope(polygon);
  const textSlope = tokenStats.slope;
  const slopeError =
    Number.isFinite(highlightSlope) && Number.isFinite(textSlope)
      ? Math.abs(highlightSlope - textSlope)
      : null;

  if (
    tokenStats.slopeSampleCount >= 2 &&
    Math.abs(textSlope) >= MIN_SLOPE_TO_CHECK &&
    slopeError > DEFAULT_SLOPE_TOLERANCE
  ) {
    issues.push({
      code: 'slope_mismatch',
      textSlope: formatNumber(textSlope),
      highlightSlope: formatNumber(highlightSlope),
      error: formatNumber(slopeError),
    });
  }

  addTokenLocalCoverageIssues(issues, polygon, tokenStats);
  addRowLocalCoverageIssues(issues, polygon, tokenStats);

  return {
    rowId: row.rowId,
    index,
    company: row.rawPharmaCompanyName ?? null,
    drugCode: row.rawDrugCode ?? null,
    ok: issues.length === 0,
    issues,
    textSlope: formatNumber(textSlope),
    highlightSlope: formatNumber(highlightSlope),
    slopeError: formatNumber(slopeError),
    sourceTokenCount: tokenStats.tokenCount,
  };
}

function buildSourceRowMap(sourceRecords) {
  const map = new Map();
  sourceRecords.forEach((record) => {
    map.set(record.rowId, record.sourceRows ?? []);
  });
  return map;
}

function buildCompanyGroups(rows) {
  const records = rows
    .flatMap((row, rowIndex) =>
      getRowPolygons(row).map((vertices) => ({
        row,
        rowIndex,
        vertices,
        ...boundsOfVertices(vertices),
      })),
    )
    .filter((record) => Number.isFinite(record.centerY))
    .sort((left, right) => left.centerY - right.centerY);

  const groups = [];
  records.forEach((record) => {
    const previous = groups[groups.length - 1];
    const last = previous?.records?.[previous.records.length - 1];
    const sameCompany =
      last?.row?.rawPharmaCompanyName === record.row.rawPharmaCompanyName;
    const yGap = last ? record.minY - last.maxY : Infinity;

    if (previous && sameCompany && yGap < ADJACENT_POLYGON_Y_GAP) {
      previous.records.push(record);
      return;
    }

    groups.push({
      company: record.row.rawPharmaCompanyName ?? null,
      records: [record],
    });
  });

  return groups.filter((group) => group.records.length >= 2);
}

function buildGroupDiagnostics(rows) {
  return buildCompanyGroups(rows).map((group, groupIndex) => {
    return {
      groupIndex,
      company: group.company,
      rowIds: group.records.map((record) => record.row.rowId),
      ok: true,
      issues: [],
      renderMode: 'compound_row_subpaths',
    };
  });
}

export function buildHighlightGeometryDiagnostics(rows, sourceRecords) {
  const sourceRowMap = buildSourceRowMap(sourceRecords ?? []);
  const rowDiagnostics = rows.map((row, index) =>
    buildRowDiagnostics(row, sourceRowMap.get(row.rowId) ?? [], index),
  );
  const groupDiagnostics = buildGroupDiagnostics(rows);
  const failedRows = rowDiagnostics.filter((diagnostic) => !diagnostic.ok);
  const failedGroups = groupDiagnostics.filter((diagnostic) => !diagnostic.ok);

  return {
    ok: failedRows.length === 0 && failedGroups.length === 0,
    failedRowCount: failedRows.length,
    failedGroupCount: failedGroups.length,
    rows: rowDiagnostics,
    groups: groupDiagnostics,
  };
}
